import { FC, useContext, useState } from 'react';
import { MapCustomControlProps } from '@app/shared/mapping/MapCustomControlProps';
import { createPortal } from 'react-dom';
import { useMapboxControl } from '@app/shared/mapping/useMapBoxControl';
import { GapDensityMapDataContext } from '@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapDataContext';
import { FlyoutLink } from '@app/shared/Flyouts';
import { GapDensityMapEditThemesFlyout } from '@app/projects/network-assessment/maps/gap-density-map/flyouts/GapDensityMapEditThemesFlyout';
import { GapDensityMapThemeContext } from '@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapThemeContext';
import { GAP_DENSITY_MAP_STYLE } from '@app/projects/network-assessment/maps/gap-density-map/GapDensityMapsConstants';
import { QESButton } from '@app/shared/qes-standards/Button';
import { QESSelect } from '@app/shared/qes-standards/forms/Select';

const GapDensityMapThemeControl: FC<MapCustomControlProps> = ({ position, ignoreInMapExport = true }) => {

  const mapDataContext = useContext(GapDensityMapDataContext);
  const themeContext = useContext(GapDensityMapThemeContext);

  const [showLayerControl, setShowLayerControl] = useState(false);

  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const [editThemesDirty, setEditThemesDirty] = useState(false);

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position });
  if (!container) {
    return null;
  }

  const warningModal = {
    body: "You will lose unsaved changes. Continue with closing the sidebar?",
    cancelButtonText: "Cancel",
    confirmButtonText: "Continue",
    title: "Warning",
  };

  return createPortal(
    <div {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <div className="gap-density-map__theme-control-main-container">
        <QESButton
          className="gap-density-map__theme-hover-container"
          onClick={() => setShowLayerControl((prev) => !prev)}
          qesTestId={"gap-density-map__theme--toggle"}>
          <img
            src={`${imagesDir}Layer_Hover.png`}
            className="gap-density-map__theme-hover-icon"
          />
        </QESButton>

        {showLayerControl &&
          <div className="gap-density-map__theme-control-container">
            <QESSelect
              label={"Map Theme"}
              testId={"gap-density-map__theme--select"}
              onChange={(e) => themeContext.setTheme(e.target.value)}
              value={themeContext.currentTheme.uuid}
              style={{
                textOverflow: "ellipsis",
              }}
            >
              <optgroup label="Quest Themes">
                {
                  GAP_DENSITY_MAP_STYLE.QUEST_THEMES.map(theme => (
                    <option
                      key={theme.uuid}
                      value={theme.uuid}
                    >
                      {theme.name}
                    </option>
                  ))
                }
              </optgroup>
              {themeContext.customThemes?.length > 0 &&
                <optgroup label="Custom Themes">
                  {
                    themeContext.customThemes.map(theme => (
                      <option
                        key={theme.uuid}
                        value={theme.uuid}
                      >
                        {theme.name}
                      </option>
                    ))
                  }
                </optgroup>
              }
            </QESSelect>
            <FlyoutLink
              classNames="qes-button-secondary gap-density-map__theme-edit"
              content={{
                body: <GapDensityMapEditThemesFlyout onDirtyChange={setEditThemesDirty} warningModal={warningModal} />,
                title: "Color Themes",
              }}
              size="40"
              testId={"gap-density-map__theme--edit-themes"}
              onClose={() => themeContext.setThemeToEdit(null)}
              warnOnClose={editThemesDirty}
              modalContent={warningModal}
            >
              Edit Themes
            </FlyoutLink>
          </div>
        }
      </div>
    </div>,
    container
  )
};

export default GapDensityMapThemeControl;
